import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import CommunicationSettings from "./CommunicationSettings";
import RediMissingContactBanner from "./RediMissingContactBanner";
import { ReactComponent as RediHealthIcon } from "../../assets/svg/redi-helth-icon.svg";
import { ReactComponent as SettingsIcon } from "../../assets/svg/settings-icon.svg";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import { useGetSetting } from "../../hooks/getSetting";
import {
  useStandardizedTracking,
  EventNames,
  FeatureGroups,
  LayoutTypes,
  RediContentKeys,
  RediFeatures,
  PageKeys
} from "../../utils/pendo/useStandardizedTracking";
import {
  StyledModal,
  ModalContainer,
  TopSection,
  HeaderRow,
  HeaderActions,
  IconContainer,
  StyledSettingsButton,
  ModalContentSection,
  ModalTitle,
  ModalDescription,
  ModalActionsSection,
  ButtonWrapper,
  ProgressBarOverlay,
  RediSecondaryButton,
  StyledPrimaryButton
} from "./styles";
import { REDI_SKIP_REASONS } from "../../constant/redi";

const COUNTDOWN_DURATION = 7; // Duration in seconds

const RediInvitationModal = ({
  visible,
  onClose,
  onLearnMore,
  onSavePreferences,
  contactInfo,
  trigger,
  drugName,
  reason,
  missingFields,
  onAddContact,
  onModalDisplayed
}) => {
  const { t } = useTranslation();
  const { track } = useStandardizedTracking();

  const [isCountdownEnabled] = useGetSetting(["fe.redi-success-message-countdown-enabled"]);

  const [countdown, setCountdown] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [defaultPreferences, setDefaultPreferences] = useState({ email: false, sms: false });

  // Track whether the close was triggered by the countdown timeout vs. manual
  const isTimeoutCloseRef = useRef(false);
  const hasReportedDisplayRef = useRef(false);

  const isMissingContact = reason === REDI_SKIP_REASONS.MISSING_CONTACT_INFO;

  const rediTrackingProps = {
    contentType: "Modal",
    featureGroup: FeatureGroups.REDI_PARTNERSHIP,
    feature: RediFeatures.REDI_INVITATION,
    layout: LayoutTypes.MODAL,
    pageKey: PageKeys.PATIENT_OVERVIEW
  };

  useEffect(() => {
    if (!visible) {
      hasReportedDisplayRef.current = false;
      return;
    }

    setCountdown(isCountdownEnabled && !isMissingContact ? COUNTDOWN_DURATION : null);
    setIsPaused(false);
    setShowSettings(false);
    isTimeoutCloseRef.current = false;

    if (contactInfo) {
      setDefaultPreferences({
        email: contactInfo.hasEmail || false,
        sms: contactInfo.hasSMS || false
      });
    }

    // Notify backend that the popup was actually displayed (for display limits).
    if (!hasReportedDisplayRef.current && onModalDisplayed) {
      hasReportedDisplayRef.current = true;
      onModalDisplayed();
    }

    // Track modal impression – use a different content key for missing-contact state.
    track(EventNames.POP_UP_IMPRESSION, {
      ...rediTrackingProps,
      contentKey: isMissingContact ? RediContentKeys.FN_MISSING_CONTACT : RediContentKeys.FN_SUCCESS_MESSAGE,
      ...(drugName && { drugName }),
      additionalInfo: {
        source: isMissingContact ? "missing_contact" : "success",
        ...(trigger && { trigger }),
        ...(isMissingContact && missingFields && { missing_fields: missingFields })
      }
    });
  }, [visible, isCountdownEnabled, contactInfo, isMissingContact, onModalDisplayed]);

  useEffect(() => {
    if (!isCountdownEnabled || countdown === null || !visible) {
      return;
    }

    // Pause countdown if paused or settings open
    if (isPaused || showSettings) {
      return;
    }

    // Stop if countdown reached zero (prevent multiple onClose calls)
    if (countdown <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          // Auto-close: mark as timeout and send invitation with default preferences
          isTimeoutCloseRef.current = true;
          setTimeout(() => handleGotIt(), 0);
          return 0;
        }
        return newValue;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, isPaused, showSettings, visible, onClose, isCountdownEnabled]);

  const handleMouseEnter = () => {
    if (isCountdownEnabled && countdown !== null) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (isCountdownEnabled && countdown !== null) {
      setIsPaused(false);
    }
  };

  const handleSettingsClick = () => {
    track(EventNames.BUTTON_CLICK, {
      ...rediTrackingProps,
      contentKey: RediContentKeys.FN_SUCCESS_SETTINGS,
      ...(drugName && { drugName }),
      additionalInfo: { action: "open_settings" }
    });
    setShowSettings(true);
  };

  const handleSettingsClose = () => {
    setShowSettings(false);
  };

  const handleSettingsSave = async (preferences) => {
    try {
      await onSavePreferences(preferences);
      setShowSettings(false);
    } catch (error) {
      ErrorMessage(t("redi.communication_settings.save_preferences_error"));
    }
  };

  const handleGotIt = async () => {
    // Track the appropriate event: timeout vs. manual click
    if (isTimeoutCloseRef.current) {
      // Billable — delivered via the backend relay
      track(EventNames.POP_UP_IMPRESSION, {
        ...rediTrackingProps,
        contentKey: RediContentKeys.FN_SUCCESS_TIMEOUT,
        transport: "backend",
        ...(drugName && { drugName }),
        additionalInfo: {
          action: "auto_close",
          countdown_seconds: COUNTDOWN_DURATION
        }
      });
    } else {
      // Billable — delivered via the backend relay
      track(EventNames.BUTTON_CLICK, {
        ...rediTrackingProps,
        contentKey: RediContentKeys.FN_SUCCESS_GOT_IT,
        transport: "backend",
        ...(drugName && { drugName }),
        additionalInfo: {
          action: "dismiss"
        }
      });
    }

    // Send invitation with default preferences (based on available contact info)
    try {
      await onSavePreferences(defaultPreferences);
    } catch (error) {
      ErrorMessage(t("redi.invitation_modal.save_preferences_error"));
    }
  };

  const handleLearnMore = () => {
    track(EventNames.BUTTON_CLICK, {
      ...rediTrackingProps,
      contentKey: RediContentKeys.FN_SUCCESS_LEARN_MORE,
      ...(drugName && { drugName }),
      additionalInfo: { action: "navigate_to_program_page" }
    });
    onLearnMore();
  };

  const handleAddContact = () => {
    track(EventNames.BUTTON_CLICK, {
      ...rediTrackingProps,
      contentKey: RediContentKeys.FN_MISSING_CONTACT_ADD,
      ...(drugName && { drugName }),
      additionalInfo: {
        action: "navigate_to_mpd",
        ...(missingFields && { missing_fields: missingFields })
      }
    });
    onAddContact?.();
  };

  const handleDismissMissingContact = () => {
    track(EventNames.BUTTON_CLICK, {
      ...rediTrackingProps,
      contentKey: RediContentKeys.FN_MISSING_CONTACT_DISMISS,
      ...(drugName && { drugName }),
      additionalInfo: { action: "dismiss_missing_contact" }
    });
    onClose();
  };

  const calculateProgress = () => {
    if (!isCountdownEnabled || countdown === null) {
      return 0;
    }
    const progress = ((COUNTDOWN_DURATION - countdown) / COUNTDOWN_DURATION) * 100;
    return Math.min(Math.max(progress, 0), 100); // Clamp between 0-100
  };

  return (
    <StyledModal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={440}
      bodyStyle={{
        padding: 0
      }}
      maskStyle={{
        backgroundColor: "rgba(0, 0, 0, 0.25)"
      }}
      style={{
        position: "fixed",
        bottom: "65px",
        right: "24px",
        top: "auto",
        margin: 0
      }}
      id="redi-invitation-modal"
    >
      <ModalContainer onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {!showSettings ? (
          <>
            <TopSection id="redi-modal-top-section" $showCountdown={isCountdownEnabled && countdown !== null}>
              <HeaderRow>
                <IconContainer>
                  <RediHealthIcon id="redi-health-icon" />
                </IconContainer>
                <HeaderActions>
                  {isMissingContact && (
                    <RediMissingContactBanner id="redi-missing-contact-banner" onAdd={handleAddContact} />
                  )}
                  <StyledSettingsButton
                    type="text"
                    shape="circle"
                    icon={<SettingsIcon id="settings-icon" />}
                    onClick={handleSettingsClick}
                    id="settings-icon-btn"
                    style={{
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  />
                </HeaderActions>
              </HeaderRow>

              <ModalContentSection id="redi-modal-content-section">
                <ModalTitle id="redi-modal-title">{t("redi.invitation_modal.title")}</ModalTitle>
                <ModalDescription id="redi-modal-description">
                  {t("redi.invitation_modal.description")}
                </ModalDescription>
              </ModalContentSection>
            </TopSection>

            <ModalActionsSection>
              <RediSecondaryButton id="redi-pause-review-btn" onClick={handleLearnMore}>
                {t("redi.invitation_modal.pause_and_review")}
              </RediSecondaryButton>
              <ButtonWrapper>
                <ProgressBarOverlay id="redi-progress-bar-overlay" $progress={calculateProgress()} />
                <StyledPrimaryButton
                  id="redi-got-it-btn"
                  type="primary"
                  onClick={isMissingContact ? handleDismissMissingContact : handleGotIt}
                >
                  {t("redi.invitation_modal.got_it")}
                </StyledPrimaryButton>
              </ButtonWrapper>
            </ModalActionsSection>
          </>
        ) : (
          <CommunicationSettings onClose={handleSettingsClose} onSave={handleSettingsSave} contactInfo={contactInfo} />
        )}
      </ModalContainer>
    </StyledModal>
  );
};

export default RediInvitationModal;
